import styles from "./style.postcss";

import React, { PureComponent } from "react";
import { bindActionCreators } from "redux";
import { List } from "immutable";
import is from "is_js";
import PropTypes from "prop-types";

import { connect, moment, PrivilegeChecker } from "omni-common-ui";

import * as SessionsAttendance from "data/SessionsAttendance";
import { actions as GroupMembers } from "data/GroupMembers";
import AttendanceCell from "./AttendanceCell";
import { calculateDefaultSession } from "data/SessionPlans/actions";
import { actions as Group } from "containers/Group";
import CourseViewTable from "components/CourseViewTable";
import FinancialClosingMoment from "domain/FinancialClosingMoment";
import Privileges from "domain/Privileges";
import Exporter from "domain/Exporter";
import { buildUrl } from "domain/Api";
import ReactGA from "react-ga4";
import { AttendanceStatus } from "data/SessionsAttendance/status";

const MARKING = "edit";
const EXPORT = "export";
const EXPORT_MIME_TYPE = "application/vnd.ms-excel";
export const NO_ACTIVE_MARKING_POPUP_TOKEN = -1;

export class MarkAttendanceCourseView extends PureComponent {
  constructor(props) {
    super(props);
    this._cellOptions = this._cellOptions.bind(this);
    this._renderCell = this._renderCell.bind(this);
    this.state = {
      activeMarkingButtonsToken: NO_ACTIVE_MARKING_POPUP_TOKEN,
    };
  }

  componentWillMount() {
    this._fetchAttendance(this.props);
  }

  componentWillReceiveProps(newProps) {
    this._fetchAttendance(newProps);
    if (newProps.params && newProps.params.mode !== MARKING) {
      this.setState({
        activeMarkingButtonsToken: NO_ACTIVE_MARKING_POPUP_TOKEN,
      });
    }
  }

  _fetchAttendance(props) {
    const { fetchGroupAttendanceIfNeeded } = props;
    fetchGroupAttendanceIfNeeded(this.props.params.groupId);
  }

  _handleShowCellMarkingButtons(activeMarkingButtonsToken) {
    this.setState({
      activeMarkingButtonsToken,
    });
  }

  _handleAttendanceSaved() {
    this.props.fetchGroupMembers(this.props.params.groupId);
  }

  _handleCloseCellMarkingButtons() {
    this.setState({
      activeMarkingButtonsToken: NO_ACTIVE_MARKING_POPUP_TOKEN,
    });
  }

  _gaChangeAttendance(studentId, currentStatus, nextStatus) {
    ReactGA.event({
      category: "Attendance",
      action: "Changed attendance",
      label: `Changed attendance from ${currentStatus} to ${nextStatus}`,
    });
  }

  _changeStudentAttendanceStatus(member, attendance, newStatus) {
    this._gaChangeAttendance(
      member.studentGeneralInfo.id,
      attendance.attendanceStatus,
      newStatus,
    );
    const { submitSessionAttendance } = this.props;
    let modifiedAttendance;
    if (attendance.attendanceStatus !== newStatus) {
      modifiedAttendance = attendance.set(
        "attendanceStatus",
        AttendanceStatus[newStatus],
      );
      const modifiedAttendanceList = new List([modifiedAttendance]);
      if (is.function(submitSessionAttendance)) {
        submitSessionAttendance(attendance.id, modifiedAttendanceList).then(
          () => this._handleAttendanceSaved(),
        );
      }
    }
    this.setState({
      isShowingMarkingButtons: false,
      modifiedAttendance,
    });
  }

  _cellOptions(member, { plan, attendance }) {
    const isMarkable =
      this.props.ignoreFinancialClosing ||
      moment().isBefore(FinancialClosingMoment.nextFrom(plan.period.start));
    if (
      !isMarkable ||
      !MarkAttendanceCourseView.isInMarkingRoute(this.props) ||
      !attendance
    ) {
      return;
    }

    const studentAttendance = attendance.find(
      (att) => att.studentGeneralInfo.id === member.studentGeneralInfo.id,
    );
    if (!studentAttendance) {
      return; // Fix for OMO-1280: No student attendance -> not markable
    }

    return Object.assign(
      {
        present: {
          label: "Present",
          onClick: () =>
            this._changeStudentAttendanceStatus(
              member,
              studentAttendance,
              AttendanceStatus.present,
            ),
        },
        absent: {
          label: "Absent",
          onClick: () =>
            this._changeStudentAttendanceStatus(
              member,
              studentAttendance,
              AttendanceStatus.absent,
            ),
        },
        late: {
          label: "Late",
          onClick: () =>
            this._changeStudentAttendanceStatus(
              member,
              studentAttendance,
              AttendanceStatus.late,
            ),
        },
      },
      this.props.canSeeNotApplicable && {
        notApplicable: {
          label: "Not Applicable",
          onClick: () =>
            this._changeStudentAttendanceStatus(
              member,
              studentAttendance,
              AttendanceStatus.notapplicable,
            ),
        },
      },
    );
  }

  _renderCell(member, { attendance: cellAttendance, plan }) {
    const studentAttendance =
      cellAttendance &&
      cellAttendance.find(
        (att) => att.studentGeneralInfo.id === member.studentGeneralInfo.id,
      );
    const attendanceStatus =
      studentAttendance && studentAttendance.attendanceStatus;
    return (
      <AttendanceCell
        attendanceStatus={attendanceStatus}
        isFutureSession={plan.isFuture()}
        key={`${member.studentGeneralInfo.id}-${plan.id}`}
      />
    );
  }

  _handleExport() {
    if (!MarkAttendanceCourseView.isInExportRoute(this.props)) return;
    const { replace, buildRoute, params, groupCode } = this.props;
    const url = buildUrl(`/groups/${params.groupId}/attendances/export`);
    const exportDate = moment().format("YYYY-MM-DD");
    const fileName = `${groupCode}_attendance_${exportDate}.xls`;

    Exporter.get(url, fileName, null, EXPORT_MIME_TYPE);
    replace(buildRoute({ mode: "" }));
  }

  render() {
    const { members, sessionPlans, attendance, timeZone } = this.props;
    if (!List.isList(members) || !List.isList(sessionPlans) || !timeZone) {
      return null;
    }

    const defaultSession = calculateDefaultSession(sessionPlans);
    const defaultSessionIndex = sessionPlans.findIndex(
      (plan) => plan.id === defaultSession.id,
    );

    this._handleExport();

    return (
      <div className={styles.MarkAttendanceCourseView}>
        <CourseViewTable
          students={members}
          highlightedColumns={[defaultSessionIndex]}
          showAch
        >
          <CourseViewTable.Header>
            <CourseViewTable.Row>
              {sessionPlans.map((plan, index) => {
                const columnId = {
                  plan,
                  index,
                  attendance: attendance.find(
                    (att, id) => id === plan.attendanceId,
                  ),
                };
                return (
                  <CourseViewTable.Cell columnId={columnId} small key={plan.id}>
                    {index + 1}
                  </CourseViewTable.Cell>
                );
              })}
            </CourseViewTable.Row>
            <CourseViewTable.Row>
              {sessionPlans.map((plan) => {
                const date = moment
                  .parseZone(plan.period.start)
                  .clone()
                  .tz(timeZone);
                return (
                  <CourseViewTable.Cell key={plan.id}>
                    <time dateTime={date.format("YYYY-MM-DD")}>
                      {date.format("ddd")}
                      <br />
                      {date.format("D")}&nbsp;{date.format("MMM")}
                    </time>
                  </CourseViewTable.Cell>
                );
              })}
            </CourseViewTable.Row>
          </CourseViewTable.Header>
          <CourseViewTable.Body cellOptions={this._cellOptions}>
            {this._renderCell}
          </CourseViewTable.Body>
        </CourseViewTable>
      </div>
    );
  }
}

MarkAttendanceCourseView.isInMarkingRoute = (props) =>
  props.params && props.params.mode === MARKING;
MarkAttendanceCourseView.isInExportRoute = (props) =>
  props.params && props.params.mode === EXPORT;

MarkAttendanceCourseView.propTypes = {
  timeZone: PropTypes.string.isRequired,
  groupCode: PropTypes.string.isRequired,
  members: PropTypes.shape().isRequired,
  sessionPlans: PropTypes.shape().isRequired,
  attendance: PropTypes.shape().isRequired,
  params: PropTypes.shape().isRequired,
  fetchGroupMembers: PropTypes.func,
  ignoreFinancialClosing: PropTypes.bool.isRequired,
  canSeeNotApplicable: PropTypes.bool.isRequired,
  fetchGroupAttendanceIfNeeded: PropTypes.func.isRequired,
  submitSessionAttendance: PropTypes.func.isRequired,
  buildRoute: PropTypes.func.isRequired,
  replace: PropTypes.func.isRequired,
};

export function mapStateToProps(state, ownProps) {
  const { groupId } = ownProps.params;
  const group = state.get("rootReducer").get("groups").get(groupId);
  const sessionPlans = new List(group.data.sessionPlans.data).map((id) =>
    state.get("rootReducer").get("sessionPlans").get(id),
  );

  return {
    timeZone: Group.getCentre(state, groupId).timeZone,
    members: GroupMembers.getMembersByGroup(state, groupId),
    groupCode: group.data.number,
    sessionPlans,
    attendance: state
      .get("rootReducer")
      .get("sessionsAttendance")
      .filter((attendance, id) =>
        sessionPlans.find(
          (session) => session && `${session.attendanceId}` === `${id}`,
        ),
      )
      .map((attendance) => attendance && attendance.data)
      .filter((attendance) => !!attendance),
    ignoreFinancialClosing: PrivilegeChecker.hasPrivilege(
      state,
      Privileges.MarkAttendance.IgnoreFinanceDate,
    ),
    canSeeNotApplicable: PrivilegeChecker.hasPrivilege(
      state,
      Privileges.MarkAttendance.MarkNotApplicable,
    ),
  };
}

function mapDispatchToProps(dispatch) {
  return Object.assign(
    bindActionCreators(SessionsAttendance.actions, dispatch),
    bindActionCreators(GroupMembers, dispatch),
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MarkAttendanceCourseView);



// WEBPACK FOOTER //
// ./containers/Group/Attendance/MarkAttendance/MarkAttendanceCourseView/component.jsx